import React, { useEffect, useState, useContext } from "react";
import axiosInstance from "../../../Configuration/axiosInstance";
import styles from "./Runs.module.css";
import LoadingSpinner from "../../../Components/Shared/Loader/Loader";
import heic2any from "heic2any";
import imageCompression from "browser-image-compression";
import UserContext from "../../../Context/UserContext";

const Runs = () => {
  const currentDateObj = new Date();
  const year = currentDateObj.getFullYear();
  const month = String(currentDateObj.getMonth() + 1).padStart(2, "0");
  const day = String(currentDateObj.getDate()).padStart(2, "0");
  const currentDate = `${year}-${month}-${day}`;

  const [runs, setRuns] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [date, setDate] = useState(currentDate);
  const [refresh, setRefresh] = useState(false);
  const [msg, setMsg] = useState();
  const [paymentTypes, setPaymentTypes] = useState({});
  const [paymentCollected, setPaymentCollected] = useState({});
  const [driverComments, setDriverComments] = useState({});

  const { user } = useContext(UserContext);

  useEffect(() => {
    setLoading(true);
    setMsg();

    if (!user?.user?._id) {
      setLoading(false);
      setMsg("User not found. Please log in again.");
      return;
    }

    const userId = user.user._id;
    console.log("Fetching deliveries for user:", userId, "on date:", date);

    axiosInstance
      .get(`/delivery/${userId}?date=${date}`)
      .then((res) => {
        console.log("Full API Response:", res.data);

        if (res.data.status === "success" && res.data.data) {
          const deliveriesData = res.data.data;
          console.log("Deliveries data:", deliveriesData);

          // ✅ UPDATED: Handle new order structure
          const runsData = deliveriesData.map((delivery) => ({
            id: delivery._id,
            run: delivery.run || `Run ${delivery._id}`,
            comments: delivery.comments || null,
            vehicle: delivery.vehicle || null,
            // ✅ FIXED: Count orders from new structure
            orderCount: delivery.order ? delivery.order.length : 0
          }));

          console.log("Processed runs data:", runsData);

          setRuns(runsData);
          setOrders(deliveriesData);

          if (runsData.length > 0) {
            setSelectedRun(runsData[0].id);
            setLoading(false);
          } else {
            setLoading(false);
            setMsg("No runs assigned to you for this date");
          }
        } else {
          setLoading(false);
          setMsg("No deliveries found for this date");
        }
      })
      .catch((error) => {
        console.error("Error fetching user deliveries:", error);
        console.log("Error details:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        });

        setLoading(false);
        setMsg("Error loading your deliveries. Please try again.");
      });

    setUploadedPhotos([]);
  }, [refresh, user, date]);

  const handleRunClick = (runId) => {
    setSelectedRun(runId);
  };

  // ✅ UPDATED: Get orders for selected run with new structure
  const getOrdersForSelectedRun = () => {
    if (selectedRun === null) {
      return [];
    }
    return orders.filter((order) => order._id === selectedRun);
  };

  // Get run details for display
  const getSelectedRunDetails = () => {
    if (selectedRun === null) return null;
    return runs.find((run) => run.id === selectedRun);
  };

  // Enhanced file change handler with validation
  const handleFileChange = (event, orderData) => {
    const files = Array.from(event.target.files);
    const maxFileSize = 10 * 1024 * 1024; // 10MB per file
    const maxTotalSize = 50 * 1024 * 1024; // 50MB total

    // ✅ FIXED: Use order ID from new structure
    const orderId = orderData.orderId?._id || orderData.orderId || orderData._id;
    console.log(`Selected ${files.length} files for order ${orderId}`);

    const oversizedFiles = files.filter((file) => file.size > maxFileSize);
    if (oversizedFiles.length > 0) {
      console.log(
        "Oversized files detected:",
        oversizedFiles.map(
          (f) => `${f.name}: ${(f.size / 1024 / 1024).toFixed(1)}MB`
        )
      );
      alert(
        `Some files are large and will be compressed automatically. This may take a moment.`
      );
    }

    const currentFiles = selectedFiles[orderId] || [];
    const totalSize = [...currentFiles, ...files].reduce(
      (sum, file) => sum + file.size,
      0
    );

    if (totalSize > maxTotalSize) {
      alert(
        "Total file size too large. Please select fewer or smaller images."
      );
      return;
    }

    console.log(`Total size: ${(totalSize / 1024 / 1024).toFixed(1)}MB`);

    setSelectedFiles({
      ...selectedFiles,
      [orderId]: [...(selectedFiles[orderId] || []), ...files],
    });
  };

  const handleRemoveFile = (orderId, index) => {
    const updatedFiles = [...selectedFiles[orderId]];
    updatedFiles.splice(index, 1);
    setSelectedFiles({ ...selectedFiles, [orderId]: updatedFiles });
  };

  // ✅ UPDATED: Handle photo upload with new order structure
  const handlePhotoUpload = async (orderData) => {
    // ✅ FIXED: Extract order ID from new structure
    const orderId = orderData.orderId?._id || orderData.orderId || orderData._id;
    const files = selectedFiles[orderId] || [];

    if (files.length === 0) {
      alert("Please select at least one photo.");
      return;
    }

    if (!user?.user?.email) {
      alert("User session expired. Please log in again.");
      return;
    }

    console.log(`Starting sequential upload of ${files.length} files for order ${orderId}...`);

    try {
      setLoading(true);
      setUploadProgress("Preparing files...");

      const uploadedUrls = [];
      let completionResponse = null;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log(
          `\n=== Processing file ${i + 1}/${files.length}: ${file.name} ===`
        );

        setUploadProgress(`Processing file ${i + 1} of ${files.length}...`);

        let processedFile = file;
        if (
          file.type === "image/heic" ||
          file.type === "image/heif" ||
          file.name.toLowerCase().endsWith(".heic")
        ) {
          try {
            console.log("Converting HEIC file:", file.name);
            setUploadProgress(
              `Converting HEIC file ${i + 1} of ${files.length}...`
            );

            const convertedBlob = await heic2any({
              blob: file,
              toType: "image/jpeg",
              quality: 0.8,
            });

            processedFile = new File(
              [convertedBlob],
              file.name.replace(/\.heic$/i, ".jpg"),
              { type: "image/jpeg" }
            );
            console.log("HEIC conversion successful");
          } catch (err) {
            console.error("Error converting HEIC file:", err);
            setUploadProgress(
              `HEIC conversion failed for file ${i + 1}, using original...`
            );
          }
        }

        let compressedFile = processedFile;
        try {
          setUploadProgress(`Compressing file ${i + 1} of ${files.length}...`);

          const isLargeFile = processedFile.size > 5 * 1024 * 1024;

          const options = {
            maxSizeMB: isLargeFile ? 0.5 : 1,
            maxWidthOrHeight: isLargeFile ? 800 : 1024,
            useWebWorker: true,
            fileType: "image/jpeg",
            quality: isLargeFile ? 0.6 : 0.8,
          };

          console.log(`Compressing file ${i + 1} with options:`, options);
          compressedFile = await imageCompression(processedFile, options);

          console.log(
            `File ${i + 1}: ${(processedFile.size / 1024).toFixed(0)}KB → ${(
              compressedFile.size / 1024
            ).toFixed(0)}KB`
          );
        } catch (err) {
          console.error(`Compression error for file ${i + 1}:`, err);
          setUploadProgress(
            `Compression failed for file ${i + 1}, using original...`
          );
        }

        const formData = new FormData();
        formData.append("photos", compressedFile);
        formData.append("userEmail", user.user.email);

        const isLastFile = i === files.length - 1;

        if (isLastFile) {
          formData.append("paymentType", paymentTypes[orderId] || "");
          formData.append(
            "paymentCollected",
            paymentCollected[orderId] || false
          );
          formData.append("driverComments", driverComments[orderId] || "");
          formData.append("completeOrder", "true");
        } else {
          formData.append("paymentType", "");
          formData.append("paymentCollected", false);
          formData.append("driverComments", "");
          formData.append("completeOrder", "false");
        }

        try {
          setUploadProgress(
            `Uploading file ${i + 1} of ${files.length} to server...`
          );
          console.log(`Uploading file ${i + 1}:`, compressedFile.name);

          const response = await axiosInstance.post(
            `/delivery/${orderId}/upload-photos`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
              timeout: 120000,
            }
          );

          console.log(`Upload ${i + 1} successful:`, response.data);

          if (response.data.signedUrls) {
            uploadedUrls.push(...response.data.signedUrls);
          }

          if (isLastFile) {
            completionResponse = response.data;
          }

          setUploadProgress(
            `File ${i + 1} of ${files.length} uploaded successfully!`
          );

          if (!isLastFile) {
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        } catch (error) {
          console.error(`Error uploading file ${i + 1}:`, error);

          let errorMessage = `Failed to upload file ${i + 1}: ${file.name}. `;

          if (error.code === "ECONNABORTED") {
            errorMessage += "Upload timed out.";
          } else if (error.response?.status === 413) {
            errorMessage += "File too large.";
          } else if (error.response?.status === 400) {
            errorMessage += "Invalid file format.";
          } else {
            errorMessage += "Network error.";
          }

          const shouldContinue =
            files.length > 1 && i < files.length - 1
              ? confirm(
                  `${errorMessage}\n\nContinue uploading remaining files?`
                )
              : false;

          if (!shouldContinue) {
            throw new Error(errorMessage);
          }

          console.log("Continuing with next file...");
        }
      }

      setDriverComments({ ...driverComments, [orderId]: "" });

      setUploadProgress("Updating order status...");

      if (uploadedUrls.length > 0) {
        setUploadedPhotos([...uploadedPhotos, ...uploadedUrls]);
      }

      setSelectedFiles({ ...selectedFiles, [orderId]: [] });

      setRefresh((prev) => !prev);

      const successMsg = `Successfully uploaded ${uploadedUrls.length} out of ${files.length} photos!`;
      alert(successMsg);

      if (completionResponse?.completedBy) {
        console.log(`Order completed by: ${completionResponse.completedBy}`);
      }
    } catch (error) {
      console.error("Sequential upload failed:", error);

      const errorMsg =
        error.message ||
        "Upload failed. Please try again 1 image or contact support.";
      alert(errorMsg);
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  const handleDate = (e) => {
    setDate(e.target.value);
  };

  const handleSearch = () => {
    setRefresh((prevState) => !prevState);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const selectedRunDetails = getSelectedRunDetails();

  return (
    <div className={styles.container}>
      <h2
        style={{ textAlign: "center", marginBottom: "2rem", fontSize: "2rem" }}
      >
        📦 Delivery Runs
      </h2>

      <div className={styles.searchStrip}>
        <input
          type="date"
          value={date}
          onChange={handleDate}
          className={styles.datePicker}
        />
        <button onClick={handleSearch} className="searchBtn">
          🔍 Search Runs
        </button>
      </div>

      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            color: "white",
          }}
        >
          <LoadingSpinner />
          {uploadProgress && (
            <div
              style={{
                marginTop: "2rem",
                textAlign: "center",
                background: "rgba(255,255,255,0.1)",
                padding: "1rem 2rem",
                borderRadius: "8px",
                backdropFilter: "blur(10px)",
              }}
            >
              <h3 style={{ margin: "0 0 1rem 0", color: "#fff" }}>
                📤 Processing Upload
              </h3>
              <p
                style={{
                  margin: "0",
                  fontSize: "1.1rem",
                  color: "#e0e0e0",
                }}
              >
                {uploadProgress}
              </p>
              <div
                style={{
                  marginTop: "1rem",
                  fontSize: "0.9rem",
                  color: "#bbb",
                }}
              >
                Please keep this tab open...
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && (
        <div>
          {runs.length > 0 ? (
            <>
              <div className={styles.runContainer}>
                <h3
                  style={{
                    width: "100%",
                    marginBottom: "1rem",
                    textAlign: "center",
                  }}
                >
                  Select a Run:
                </h3>
                {runs.map((run) => (
                  <div
                    key={run.id}
                    onClick={() => handleRunClick(run.id)}
                    className={
                      run.id === selectedRun ? styles.activeRun : styles.runText
                    }
                    style={{
                      cursor: "pointer",
                      padding: "1rem",
                      margin: "0.5rem",
                      borderRadius: "8px",
                      border: run.id === selectedRun ? "2px solid #007bff" : "1px solid #ddd",
                      background: run.id === selectedRun ? "#f8f9fa" : "white",
                    }}
                  >
                    {/* ✅ Enhanced run header with name and basic info */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: run.comments ? "0.75rem" : "0",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: "1.1rem",
                            fontWeight: "600",
                            color: run.id === selectedRun ? "#007bff" : "#333",
                            marginBottom: "0.25rem",
                          }}
                        >
                          {run.run}
                        </div>
                        <div
                          style={{
                            fontSize: "0.85rem",
                            color: "#666",
                            display: "flex",
                            gap: "1rem",
                          }}
                        >
                          {run.vehicle && (
                            <span>🚚 {run.vehicle}</span>
                          )}
                          <span>📦 {run.orderCount} order{run.orderCount !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                    </div>

                    {/* ✅ Display delivery comments if they exist */}
                    {run.comments && (
                      <div
                        style={{
                          background: run.id === selectedRun ? "#e3f2fd" : "#f8f9fa",
                          padding: "0.75rem",
                          borderRadius: "6px",
                          border: `1px solid ${run.id === selectedRun ? "#bbdefb" : "#e9ecef"}`,
                          marginTop: "0.5rem",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            color: "#666",
                            marginBottom: "0.25rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          💬 Delivery Notes:
                        </div>
                        <div
                          style={{
                            fontSize: "0.85rem",
                            color: "#495057",
                            lineHeight: "1.4",
                          }}
                        >
                          {run.comments}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* ✅ Enhanced selected run header with delivery comments */}
              {selectedRunDetails && (
                <div
                  style={{
                    background: "#f8f9fa",
                    padding: "1.5rem",
                    borderRadius: "8px",
                    margin: "1rem 0",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: selectedRunDetails.comments ? "1rem" : "0",
                    }}
                  >
                    <div>
                      <h3
                        style={{
                          margin: "0 0 0.5rem 0",
                          color: "#007bff",
                          fontSize: "1.5rem",
                        }}
                      >
                        📋 {selectedRunDetails.run}
                      </h3>
                      <div
                        style={{
                          fontSize: "0.95rem",
                          color: "#666",
                          display: "flex",
                          gap: "1.5rem",
                          flexWrap: "wrap",
                        }}
                      >
                        {selectedRunDetails.vehicle && (
                          <span>🚚 Vehicle: {selectedRunDetails.vehicle}</span>
                        )}
                        <span>📦 Orders: {selectedRunDetails.orderCount}</span>
                      </div>
                    </div>
                  </div>

                  {/* ✅ Display delivery comments prominently for selected run */}
                  {selectedRunDetails.comments && (
                    <div
                      style={{
                        background: "#fff3cd",
                        padding: "1rem",
                        borderRadius: "6px",
                        border: "1px solid #ffeaa7",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.9rem",
                          fontWeight: "600",
                          color: "#856404",
                          marginBottom: "0.5rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        💬 Delivery Instructions:
                      </div>
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: "#856404",
                          lineHeight: "1.5",
                          fontWeight: "500",
                        }}
                      >
                        {selectedRunDetails.comments}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className={styles.ordersList}>
                {/* ✅ UPDATED: Handle new order structure */}
                {getOrdersForSelectedRun().map((delivery) =>
                  delivery?.order
                    ?.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)) // ✅ Sort by displayOrder
                    ?.map((orderItem, index) => {
                      // ✅ FIXED: Extract order data from new structure
                      const order = orderItem.orderId; // The actual order data is in orderId
                      const orderId = order?._id;

                      if (!order) {
                        console.warn("Order data not found for orderItem:", orderItem);
                        return null;
                      }

                      return (
                        <div key={orderId || index} className={styles.orderCard}>
                          <div className={styles.customerName}>
                            {order?.customer?.businessName}
                          </div>

                          <div className={styles.productsList}>
                            <h4 style={{ margin: "0 0 0.5rem 0", color: "#666" }}>
                              Order Items:
                            </h4>
                            {order?.products?.length > 0 ? (
                              order.products.map((product, idx) => (
                                <div key={idx} className={styles.productRow}>
                                  <span>
                                    {product?.product?.productName ||
                                      product?.productName ||
                                      `Product ${idx + 1}`}
                                  </span>
                                  <span
                                    style={{ fontWeight: "bold", color: "#007bff" }}
                                  >
                                    × {product?.quantity || 0}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <div style={{ color: "#999", fontStyle: "italic" }}>
                                No products found in this order
                              </div>
                            )}
                          </div>

                          <div className={styles.commentsSection}>
                            {order?.comments && (
                              <div className={styles.orderComments}>
                                <h4
                                  style={{
                                    margin: "0 0 0.5rem 0",
                                    color: "#666",
                                    fontSize: "0.9rem",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                  }}
                                >
                                  📝 Order Notes:
                                </h4>
                                <div
                                  style={{
                                    background: "#f8f9fa",
                                    padding: "0.75rem",
                                    borderRadius: "6px",
                                    border: "1px solid #e9ecef",
                                    fontSize: "0.85rem",
                                    color: "#495057",
                                    lineHeight: "1.4",
                                    marginBottom: "0.75rem",
                                  }}
                                >
                                  {order.comments}
                                </div>
                              </div>
                            )}

                            {order?.customer?.comments && (
                              <div className={styles.customerComments}>
                                <h4
                                  style={{
                                    margin: "0 0 0.5rem 0",
                                    color: "#666",
                                    fontSize: "0.9rem",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                  }}
                                >
                                  👤 Customer Notes:
                                </h4>
                                <div
                                  style={{
                                    background: "#e8f4f8",
                                    padding: "0.75rem",
                                    borderRadius: "6px",
                                    border: "1px solid #b8daff",
                                    fontSize: "0.85rem",
                                    color: "#495057",
                                    lineHeight: "1.4",
                                    marginBottom: "0.75rem",
                                  }}
                                >
                                  {order.customer.comments}
                                </div>
                              </div>
                            )}

                            {!order?.comments && !order?.customer?.comments && (
                              <div
                                style={{
                                  background: "#f8f9fa",
                                  padding: "0.5rem 0.75rem",
                                  borderRadius: "6px",
                                  border: "1px solid #e9ecef",
                                  fontSize: "0.8rem",
                                  color: "#6c757d",
                                  fontStyle: "italic",
                                  marginBottom: "0.75rem",
                                  textAlign: "center",
                                }}
                              >
                                No special notes for this order or customer
                              </div>
                            )}
                          </div>

                          {order.status === "Completed" ? (
                            <div className={styles.completedSection}>
                              <div className={styles.completedBadge}>
                                ✅ Order Completed
                              </div>

                              {order.driverComments && (
                                <div
                                  style={{
                                    marginTop: "1rem",
                                    padding: "0.75rem",
                                    background: "#d4edda",
                                    borderRadius: "6px",
                                    border: "1px solid #c3e6cb",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontWeight: "600",
                                      marginBottom: "0.5rem",
                                      color: "#155724",
                                      fontSize: "0.9rem",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.5rem",
                                    }}
                                  >
                                    🚚 Driver Comments:
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.85rem",
                                      color: "#155724",
                                      lineHeight: "1.4",
                                    }}
                                  >
                                    {order.driverComments}
                                  </div>
                                </div>
                              )}

                              {(order.paymentType ||
                                order.paymentCollected !== null) && (
                                <div
                                  style={{
                                    marginTop: "1rem",
                                    padding: "0.75rem",
                                    background: "#e8f5e8",
                                    borderRadius: "6px",
                                    border: "1px solid #c3e6c3",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "0.85rem",
                                      color: "#155724",
                                    }}
                                  >
                                    💳 Payment:{" "}
                                    {order.paymentType || "Not specified"}
                                    {order.paymentCollected !== null && (
                                      <span style={{ marginLeft: "1rem" }}>
                                        💰{" "}
                                        {order.paymentCollected
                                          ? "Collected ✅"
                                          : "Not Collected ❌"}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "1rem",
                              }}
                            >
                              <div
                                style={{
                                  borderTop: "2px solid #e9ecef",
                                  paddingTop: "1rem",
                                  marginTop: "0.5rem",
                                }}
                              >
                                <div>
                                  <label
                                    style={{
                                      fontWeight: "600",
                                      marginBottom: "0.5rem",
                                      display: "block",
                                    }}
                                  >
                                    💳 Payment Method:
                                  </label>
                                  <select
                                    value={paymentTypes[orderId] || ""}
                                    onChange={(e) =>
                                      setPaymentTypes({
                                        ...paymentTypes,
                                        [orderId]: e.target.value,
                                      })
                                    }
                                    style={{
                                      width: "100%",
                                      padding: "0.5rem",
                                      borderRadius: "4px",
                                      border: "1px solid #ddd",
                                    }}
                                  >
                                    <option value="">Select Payment Type</option>
                                    <option value="Cash">Cash</option>
                                    <option value="Card">Card</option>
                                    <option value="EFT">EFT</option>
                                  </select>
                                </div>

                                <label
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={paymentCollected[orderId] || false}
                                    onChange={(e) =>
                                      setPaymentCollected({
                                        ...paymentCollected,
                                        [orderId]: e.target.checked,
                                      })
                                    }
                                  />
                                  💰 Payment Collected
                                </label>

                                <div>
                                  <label
                                    style={{
                                      fontWeight: "600",
                                      marginBottom: "0.5rem",
                                      display: "block",
                                    }}
                                  >
                                    💬 Driver Comments:
                                  </label>
                                  <textarea
                                    value={driverComments[orderId] || ""}
                                    onChange={(e) =>
                                      setDriverComments({
                                        ...driverComments,
                                        [orderId]: e.target.value,
                                      })
                                    }
                                    placeholder="Add any delivery notes, customer feedback, or special instructions..."
                                    style={{
                                      width: "100%",
                                      padding: "0.75rem",
                                      borderRadius: "4px",
                                      border: "1px solid #ddd",
                                      minHeight: "80px",
                                      resize: "vertical",
                                      fontFamily: "inherit",
                                      fontSize: "0.9rem",
                                    }}
                                    maxLength={500}
                                  />
                                  <div
                                    style={{
                                      fontSize: "0.8rem",
                                      color: "#666",
                                      textAlign: "right",
                                      marginTop: "0.25rem",
                                    }}
                                  >
                                    {(driverComments[orderId] || "").length}/500
                                    characters
                                  </div>
                                </div>

                                <div>
                                  <h4
                                    style={{ margin: "0 0 1rem 0", color: "#666" }}
                                  >
                                    📸 Delivery Photos:
                                  </h4>

                                  {selectedFiles[orderId] &&
                                    selectedFiles[orderId].length > 0 && (
                                      <div style={{ marginBottom: "1rem" }}>
                                        <div
                                          style={{
                                            fontSize: "0.9rem",
                                            color: "#666",
                                            marginBottom: "0.5rem",
                                          }}
                                        >
                                          Selected files (
                                          {selectedFiles[orderId].length}):
                                        </div>
                                        {selectedFiles[orderId].map(
                                          (file, fileIndex) => (
                                            <div
                                              key={fileIndex}
                                              style={{
                                                fontSize: "0.8rem",
                                                color: "#888",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                padding: "0.25rem 0",
                                              }}
                                            >
                                              <span>{file.name}</span>
                                              <span>
                                                {formatFileSize(file.size)}
                                              </span>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    )}

                                  <div className={styles.photosContainer}>
                                    {selectedFiles[orderId] &&
                                      selectedFiles[orderId].map(
                                        (file, fileIndex) => (
                                          <div
                                            key={fileIndex}
                                            className={styles.photoItem}
                                          >
                                            <img
                                              src={URL.createObjectURL(file)}
                                              alt={`Selected ${fileIndex}`}
                                            />
                                            <button
                                              onClick={() =>
                                                handleRemoveFile(
                                                  orderId,
                                                  fileIndex
                                                )
                                              }
                                              className={styles.deleteButton}
                                            >
                                              🗑️ Remove
                                            </button>
                                          </div>
                                        )
                                      )}

                                    <label className={styles.addPhotoLabel}>
                                      <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        multiple
                                        onChange={(e) => handleFileChange(e, orderItem)}
                                      />
                                      <span className={styles.addIcon}>📷</span>
                                    </label>
                                  </div>
                                </div>

                                <button
                                  onClick={() => handlePhotoUpload(orderItem)}
                                  className={styles.uploadButton}
                                  disabled={
                                    !selectedFiles[orderId] ||
                                    selectedFiles[orderId].length === 0 ||
                                    loading
                                  }
                                  style={{
                                    opacity:
                                      !selectedFiles[orderId] ||
                                      selectedFiles[orderId].length === 0 ||
                                      loading
                                        ? 0.6
                                        : 1,
                                  }}
                                >
                                  {loading
                                    ? "🔄 Processing..."
                                    : "📤 Complete Order & Upload Photos"}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                )}
              </div>
            </>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "3rem",
                fontSize: "1.2rem",
                color: "#666",
              }}
            >
              {msg || "No runs found for this date"}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Runs;